import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { fetchAllTestCaseData } from '../../../utils/api';
import { debounce } from '../../../utils/commonfunctions';
import { useParams } from 'react-router-dom';
import { getOptionValue, mergeUrlTestCaseIntoOptions } from '../utils/optionHelpers';

/**
 * Custom hook for managing test case search functionality
 * Handles search, pagination, and loading states
 */
export const useTestCaseSearch = ({
    restrictToUrlTestCase = true,
    selectedTestCaseOption = null,
} = {}) => {
    const [testCaseOptions, setTestCaseOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTestCase, setSearchTestCase] = useState('');
    const abortControllerRef = useRef(null);

    const selectedTestCaseOptionRef = useRef(selectedTestCaseOption);

    useEffect(() => {
        selectedTestCaseOptionRef.current = selectedTestCaseOption;
    }, [selectedTestCaseOption]);

    const isMountedRef = useRef(true);
    const params = useParams();
    const urlTestCaseId = params?.test_case_id;

    const mergeWithSelected = useCallback(
        (options) => {
            const selected = selectedTestCaseOptionRef.current;
            if (!selected?.id) return options;
            if (urlTestCaseId && String(getOptionValue(selected)) !== String(urlTestCaseId)) {
                return options;
            }
            return mergeUrlTestCaseIntoOptions(options, selected);
        },
        [urlTestCaseId]
    );

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const debouncedSearch = useMemo(
        () => {
            return debounce(async (term, page = 1, append = false) => {
                if (typeof term !== 'string') return;
                if (!term || term.trim().length === 0) {
                    if (page === 1 && !append) {
                        // Initial load is allowed
                    } else {
                        return;
                    }
                }

                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }

                abortControllerRef.current = new AbortController();
                const signal = abortControllerRef.current.signal;

                if (!isMountedRef.current) return;

                setLoading(true);
                setApiError(null);

                try {
                    const searchParams = {
                        page: Math.max(1, parseInt(page, 10) || 1),
                        limit: 15
                    };

                    if (term && term.trim()) {
                        searchParams.testCaseTitle = term.trim();
                        searchParams.testCaseName = term.trim();
                    }

                    if (urlTestCaseId && restrictToUrlTestCase) {
                        searchParams.testCaseId = urlTestCaseId;
                    }

                    const response = await fetchAllTestCaseData({
                        ...searchParams,
                        onlyInstructionAvailable: true,
                        signal
                    });

                    if (!isMountedRef.current) return;

                    if (response.status === 1 && response.data) {
                        let newOptions = [];
                        let pagination = {};

                        if (response.data.success && response.data.data) {
                            newOptions = Array.isArray(response.data.data.results)
                                ? response.data.data.results
                                : [];
                            pagination = response.data.data.pagination || {};
                        } else if (Array.isArray(response.data.results)) {
                            newOptions = response.data.results;
                            pagination = response.data.pagination || {};
                        } else if (Array.isArray(response.data)) {
                            newOptions = response.data;
                            pagination = {};
                        }

                        if (!append) {
                            setTestCaseOptions(mergeWithSelected(newOptions));
                        } else {
                            setTestCaseOptions(prevOptions => {
                                const existingIds = new Set(prevOptions.map(opt => String(getOptionValue(opt))));
                                const uniqueNew = newOptions.filter(
                                    opt => !existingIds.has(String(getOptionValue(opt)))
                                );
                                return mergeWithSelected([...prevOptions, ...uniqueNew]);
                            });
                        }
                        setHasMore(Boolean(pagination.hasMore));
                        setCurrentPage(page);

                        if (newOptions.length === 0 && !append) {
                            setApiError(null);
                        }
                    } else {
                        const errorMessage = response.error || 'Failed to fetch test cases';
                        setApiError(errorMessage);
                        if (!append) {
                            setTestCaseOptions(mergeWithSelected([]));
                        }
                        setHasMore(false);
                    }
                } catch (error) {
                    if (!isMountedRef.current) return;

                    if (error.name === 'AbortError' || error.name === 'CanceledError') {
                        return;
                    }
                    const errorMessage = error.response?.data?.message || error.message || 'Error searching test cases';
                    setApiError(errorMessage);
                    if (!append) {
                        setTestCaseOptions(mergeWithSelected([]));
                    }
                    setHasMore(false);
                } finally {
                    if (isMountedRef.current) {
                        setLoading(false);
                    }
                }
            }, 300);
        },
        [mergeWithSelected, restrictToUrlTestCase, urlTestCaseId]
    );

    const handleInputChange = useCallback((inputValue) => {
        setSearchTestCase(inputValue);
        setCurrentPage(1);
        setHasMore(false);
        debouncedSearch(inputValue, 1, false);
    }, [debouncedSearch]);

    const loadMore = useCallback(() => {
        if (hasMore && !loading) {
            debouncedSearch(searchTestCase, currentPage + 1, true);
        }
    }, [hasMore, loading, searchTestCase, currentPage, debouncedSearch]);

    const handleKeyboardNavigation = useCallback((direction, currentIndex, totalOptions) => {
        const threshold = 3;

        if (direction === 'down' && hasMore && !loading) {
            if (currentIndex >= totalOptions - threshold) {
                loadMore();
            }
        }
    }, [hasMore, loading, loadMore]);

    const handleDropdownScroll = useCallback((e) => {
        const { scrollHeight, scrollTop, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !loading) {
            loadMore();
        }
    }, [hasMore, loading, loadMore]);

    useEffect(() => {
        debouncedSearch('', 1, false);
    }, [debouncedSearch]);

    useEffect(() => {
        if (!selectedTestCaseOption?.id) return;
        if (
            urlTestCaseId &&
            String(getOptionValue(selectedTestCaseOption)) !== String(urlTestCaseId)
        ) {
            return;
        }
        setTestCaseOptions((prev) => {
            const selectedId = String(getOptionValue(selectedTestCaseOption));
            const existingIndex = prev.findIndex(
                (opt) => String(getOptionValue(opt)) === selectedId
            );
            if (existingIndex >= 0) {
                const next = [...prev];
                next[existingIndex] = selectedTestCaseOption;
                return next;
            }
            return mergeUrlTestCaseIntoOptions(prev, selectedTestCaseOption);
        });
    }, [selectedTestCaseOption, urlTestCaseId]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            if (debouncedSearch?.cancel) {
                debouncedSearch.cancel();
            }
        };
    }, [debouncedSearch]);

    return {
        testCaseOptions,
        loading,
        apiError,
        hasMore,
        searchTestCase,
        handleInputChange,
        loadMore,
        handleKeyboardNavigation,
        handleDropdownScroll,
        setApiError
    };
};
